import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, HandHeart, BookOpen, Megaphone, Hammer, Cross, Lock, ShieldCheck, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/client';
import Seo from '@/seo/Seo';
import { breadcrumbSchema } from '@/seo/schemas';
import SectionTitle from '@/components/SectionTitle';

const CATEGORIES = [
  { id: 'tithe_offering',  label: 'Tithe & Offering',         icon: Heart,     suggested: [5, 10, 20, 50, 100, 200],         cta: 'Give Offering',
    blurb: 'Support the regular ministry life of the church — weekly worship, prayer gatherings, resources, pastoral work and ongoing church needs.' },
  { id: 'general_giving',  label: 'General Giving',           icon: Sparkles,  suggested: [50, 100, 200, 300, 500, 1000],    cta: 'Give Generously',
    blurb: 'Sow into the wider mission of Ministry of Jesus — church growth, ministry development, discipleship resources and Spirit-filled gatherings.' },
  { id: 'evangelism',      label: 'Evangelism & Outreach',    icon: Megaphone, suggested: [10, 20, 50, 100, 200],            cta: 'Support Evangelism',
    blurb: 'Help carry the Gospel into the community — outreach events, local mission, Gospel materials and acts of compassion.' },
  { id: 'sunday_school',   label: 'Sunday School & VBS',      icon: BookOpen,  suggested: [10, 20, 50, 100, 200],            cta: 'Support Children',
    blurb: 'Invest in children and families — Sunday School, Vacation Bible School, teaching materials, crafts and safe family ministry.' },
  { id: 'prayer_ministry', label: 'Prayer & Ministry Support',icon: HandHeart, suggested: [10, 20, 50, 100, 200],            cta: 'Support Prayer Ministry',
    blurb: 'Strengthen prayer ministry and pastoral care — altar ministry resources, discipleship sessions and spiritual growth gatherings.' },
  { id: 'building_media',  label: 'Building, Media & Equipment', icon: Hammer, suggested: [50, 100, 200, 500, 1000],         cta: 'Support Church Growth',
    blurb: 'Help provide spaces and tools for ministry — worship equipment, livestream tools, church setup, maintenance and infrastructure.' },
];

const FREQUENCIES = [
  { id: 'one_time', label: 'One Time', available: true,  hint: 'A single gift now.' },
  { id: 'weekly',   label: 'Weekly',   available: true,  hint: 'Charged every week. Cancel anytime.' },
  { id: 'monthly',  label: 'Monthly',  available: true,  hint: 'Charged every month. Cancel anytime.' },
];

const FAQS = [
  { q: 'Is online giving secure?',
    a: 'Online donations are processed through Stripe, a globally trusted payment provider. Card details are never seen or stored by Ministry of Jesus — they are handled entirely by Stripe on a PCI-compliant page.' },
  { q: 'Can a donation be made regularly?',
    a: 'Yes — choose Weekly or Monthly during checkout to set up recurring giving. You can cancel any time from the link in your Stripe receipt email or by contacting us.' },
  { q: 'Can a donation be given for a specific ministry?',
    a: 'Yes — choose from Tithe & Offering, General Giving, Evangelism, Sunday School & VBS, Prayer Ministry, or Building & Equipment.' },
  { q: 'Can a receipt be issued?',
    a: 'Yes — Stripe automatically emails a receipt to the email address you provide. We also keep a record for our charity reporting.' },
  { q: 'Can prayer notes be added?',
    a: 'Yes — you may include an optional comment or prayer note with your donation and our intercessors will pray over it.' },
];

const EMPTY_DONOR = { donor_name: '', donor_email: '', donor_phone: '', prayer_note: '', consent_given: false };

export default function Donate() {
  const [categoryId, setCategoryId] = useState(CATEGORIES[0].id);
  const [frequency, setFrequency] = useState('one_time');
  const [amountChoice, setAmountChoice] = useState(null); // null = none selected
  const [customAmount, setCustomAmount] = useState('');
  const [donor, setDonor] = useState(EMPTY_DONOR);
  const [submitting, setSubmitting] = useState(false);

  const category = useMemo(() => CATEGORIES.find((c) => c.id === categoryId), [categoryId]);

  const finalAmount = useMemo(() => {
    const c = parseFloat(customAmount);
    if (!isNaN(c) && c > 0) return c;
    if (amountChoice) return amountChoice;
    return 0;
  }, [amountChoice, customAmount]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 2) {
      toast.error('Please choose or enter a valid donation amount (minimum €2).');
      return;
    }
    if (finalAmount > 10000) {
      toast.error('For donations above €10,000 please contact us directly so we can support you properly.');
      return;
    }
    if (!donor.donor_name.trim() || !donor.donor_email.trim()) {
      toast.error('Please share your name and email so we can send your receipt.');
      return;
    }
    if (!donor.consent_given) {
      toast.error('Please confirm consent to proceed.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('/donations/create-session', {
        category: categoryId,
        frequency,
        amount: finalAmount,
        ...donor,
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error('Stripe did not return a redirect URL.');
      // Redirect the donor to Stripe Checkout.
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not start checkout. Please try again.';
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo
        title="Donate — Support Ministry of Jesus, Dublin"
        titleExact
        description="Give to the work of God at Ministry of Jesus, a Pentecostal church in Dublin (Registered Charity RCN 20207036). Support worship, prayer, evangelism, children's ministry and community care across Dublin, Kildare, Meath and Wicklow."
        path="/donate"
        keywords={['donate Ministry of Jesus', 'church donation Dublin', 'Pentecostal church giving Ireland', 'charity RCN 20207036']}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Donate', path: '/donate' }])]}
      />

      {/* HERO */}
      <section className="gradient-purple text-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mb-5 border border-moj-gold/30">
            <Heart size={14}/> Give to the Work of God
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4 leading-tight">Donate</h1>
          <p className="text-lg text-white/90 max-w-3xl">
            Support worship, prayer, evangelism, children’s ministry, community care, and the mission of Ministry of Jesus in Ireland.
          </p>
          <p className="italic text-moj-gold/90 mt-6 max-w-3xl border-l-2 border-moj-gold/50 pl-4">
            “God loves a cheerful giver.” — 2 Corinthians 9:7
          </p>
        </div>
      </section>

      {/* OPENING MESSAGE */}
      <section className="py-12 bg-moj-cream-soft">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-moj-purple-deep mb-3">Thanksgiving for every generous gift</h2>
          <p className="text-moj-charcoal/85 leading-relaxed">
            Every donation helps Ministry of Jesus continue the work of worship, prayer, Bible teaching, evangelism, children’s ministry,
            pastoral care, and community outreach. Giving is an act of faith, gratitude, and partnership in the Gospel.
          </p>
        </div>
      </section>

      {/* CATEGORY PICKER */}
      <section className="py-16 bg-moj-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle kicker="Step 1" title="Choose a Giving Option" center
            subtitle="Select where your gift goes. You can change this at any time before checkout."/>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {CATEGORIES.map((c) => {
              const active = c.id === categoryId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setCategoryId(c.id); setAmountChoice(null); setCustomAmount(''); }}
                  data-testid={`donate-category-${c.id}`}
                  aria-pressed={active}
                  className={`card-moj p-6 text-left transition focus:outline-none focus:ring-2 focus:ring-moj-gold ${
                    active ? 'ring-2 ring-moj-purple shadow-lg' : 'hover:shadow-md'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${active ? 'gradient-purple-gold' : 'gradient-purple'}`}>
                    <c.icon size={22} className="text-moj-gold"/>
                  </div>
                  <h3 className="font-serif text-lg text-moj-purple-deep mb-2">{c.label}</h3>
                  <p className="text-sm text-moj-charcoal/80 leading-relaxed">{c.blurb}</p>
                  <div className={`mt-4 text-xs font-bold uppercase tracking-wider ${active ? 'text-moj-purple-deep' : 'text-moj-gold-deep'}`}>
                    {active ? '✓ Selected' : c.cta}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="py-16 gradient-soft-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionTitle kicker="Step 2" title="Make a Donation" center
            subtitle={`You’re giving to: ${category.label}`}/>

          <form onSubmit={onSubmit} className="card-moj p-6 sm:p-8 space-y-6" data-testid="donate-form">
            {/* Frequency */}
            <fieldset>
              <legend className="label-moj mb-2">Choose Frequency <span className="text-red-600">*</span></legend>
              <div className="flex flex-wrap gap-3">
                {FREQUENCIES.map((f) => {
                  const selected = f.id === frequency;
                  const disabled = !f.available;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => f.available && setFrequency(f.id)}
                      disabled={disabled}
                      aria-pressed={selected}
                      title={f.hint}
                      data-testid={`donate-frequency-${f.id}`}
                      className={`px-5 py-2 rounded-full border-2 text-sm font-semibold transition ${
                        disabled
                          ? 'border-moj-gold/20 text-moj-charcoal/40 cursor-not-allowed bg-moj-cream-soft'
                          : selected
                            ? 'border-moj-purple bg-moj-purple text-white'
                            : 'border-moj-gold/40 text-moj-purple-deep hover:bg-moj-cream'
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Amount */}
            <fieldset>
              <legend className="label-moj mb-2">Choose Amount <span className="text-red-600">*</span></legend>
              <div className="flex flex-wrap gap-2">
                {category.suggested.map((a) => {
                  const selected = a === amountChoice && !customAmount;
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => { setAmountChoice(a); setCustomAmount(''); }}
                      aria-pressed={selected}
                      data-testid={`donate-amount-${a}`}
                      className={`px-4 py-2 rounded-full border-2 text-sm font-semibold transition ${
                        selected
                          ? 'border-moj-gold-deep bg-moj-gold/30 text-moj-purple-deep'
                          : 'border-moj-gold/40 text-moj-purple-deep hover:bg-moj-cream'
                      }`}
                    >
                      €{a}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3">
                <label className="label-moj" htmlFor="donate-custom">Other Amount (€)</label>
                <input
                  id="donate-custom"
                  type="number"
                  inputMode="decimal"
                  min={2}
                  max={10000}
                  step="0.01"
                  className="input-moj"
                  placeholder="e.g. 75"
                  value={customAmount}
                  onChange={(e) => { setCustomAmount(e.target.value); setAmountChoice(null); }}
                  data-testid="donate-custom-amount"
                />
                <p className="text-xs text-moj-charcoal/60 mt-1">Minimum €2 · Maximum €10,000 per transaction.</p>
              </div>
              {finalAmount > 0 && (
                <div className="mt-3 inline-flex items-center gap-2 bg-moj-purple/5 text-moj-purple-deep px-3 py-1.5 rounded-full text-sm">
                  <Heart size={14} className="text-moj-gold-deep"/> Giving <strong>€{finalAmount.toFixed(2)}</strong>
                  {frequency !== 'one_time' && <span> every <strong>{frequency === 'weekly' ? 'week' : 'month'}</strong></span>}
                  <span> to {category.label}</span>
                </div>
              )}
              {frequency !== 'one_time' && (
                <div className="mt-3 text-xs text-moj-charcoal/70 bg-moj-gold/10 border border-moj-gold/30 px-3 py-2 rounded-lg">
                  <strong className="text-moj-purple-deep">Recurring gift:</strong> you'll be charged €{finalAmount > 0 ? finalAmount.toFixed(2) : '—'} every {frequency === 'weekly' ? 'week' : 'month'} until you cancel. You can cancel at any time from the link in your Stripe receipt email or by contacting us.
                </div>
              )}
            </fieldset>

            {/* Donor details */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-moj" htmlFor="donate-name">Full Name <span className="text-red-600">*</span></label>
                <input id="donate-name" required className="input-moj" value={donor.donor_name}
                  autoComplete="name"
                  onChange={(e) => setDonor({ ...donor, donor_name: e.target.value })}
                  data-testid="donate-name"/>
              </div>
              <div>
                <label className="label-moj" htmlFor="donate-email">Email Address <span className="text-red-600">*</span></label>
                <input id="donate-email" type="email" required className="input-moj" value={donor.donor_email}
                  autoComplete="email" inputMode="email"
                  onChange={(e) => setDonor({ ...donor, donor_email: e.target.value })}
                  data-testid="donate-email"/>
              </div>
            </div>
            <div>
              <label className="label-moj" htmlFor="donate-phone">Phone Number</label>
              <input id="donate-phone" type="tel" inputMode="tel" autoComplete="tel"
                className="input-moj" value={donor.donor_phone}
                onChange={(e) => setDonor({ ...donor, donor_phone: e.target.value })}
                data-testid="donate-phone"/>
            </div>
            <div>
              <label className="label-moj" htmlFor="donate-note">Comment or Prayer Note (optional)</label>
              <textarea id="donate-note" rows={3} className="input-moj" value={donor.prayer_note}
                onChange={(e) => setDonor({ ...donor, prayer_note: e.target.value })}
                placeholder="Lord, please..."
                data-testid="donate-note"/>
            </div>

            {/* Consent row — enlarged tap target for mobile. The whole label is
                a 44px+ tap row, and the checkbox itself is 20px for clarity. */}
            <label className="flex items-start gap-3 text-sm text-moj-charcoal cursor-pointer py-2 -my-2 touch-manipulation">
              <input type="checkbox" required
                className="mt-0.5 h-5 w-5 shrink-0 accent-moj-purple cursor-pointer"
                checked={donor.consent_given}
                onChange={(e) => setDonor({ ...donor, consent_given: e.target.checked })}
                data-testid="donate-consent"/>
              <span className="leading-relaxed">
                Consent given for Ministry of Jesus to process donation details for church administration, receipt records and donor communication. See our <Link to="/privacy" className="text-moj-purple underline">Privacy Notice</Link>.
              </span>
            </label>

            <div className="flex items-start gap-2 text-xs text-moj-charcoal/70 bg-moj-cream-soft p-3 rounded-lg">
              <Lock size={14} className="text-moj-gold-deep mt-0.5 shrink-0"/>
              <span>
                Payments are processed securely by <strong>Stripe</strong>. Card details are never seen or stored by Ministry of Jesus. You will be redirected to Stripe’s secure checkout page to complete your gift.
              </span>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center text-base"
              data-testid="donate-submit"
            >
              {submitting ? <><Loader2 className="animate-spin" size={18}/> Redirecting to Stripe…</> : <><Heart size={18}/> Donate Now</>}
            </button>
          </form>
        </div>
      </section>

      {/* CHARITY TAX RELIEF */}
      <section className="py-12 bg-moj-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 card-moj p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-3">
            <ShieldCheck size={26} className="text-moj-gold-deep mt-0.5"/>
            <h2 className="font-serif text-2xl text-moj-purple-deep">Charity Tax Relief (Ireland)</h2>
          </div>
          <p className="text-moj-charcoal/85">
            Eligible annual donations may qualify for charity tax relief where applicable. For Irish approved bodies, Revenue states that
            qualifying donations from individuals may allow the approved body to claim tax relief when authorised by the donor. The current
            minimum qualifying annual donation amount is <strong>€250</strong> at a relief rate of <strong>31%</strong>.
          </p>
          <p className="text-moj-charcoal/85 mt-3">
            Ministry of Jesus can provide the required charity tax form once registration and eligibility details are confirmed — just
            <Link to="/contact" className="text-moj-purple underline ml-1">get in touch</Link>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-moj-cream-soft">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionTitle kicker="Frequently asked" title="Giving FAQ" center/>
          <dl className="space-y-4 mt-8">
            {FAQS.map((f) => (
              <div key={f.q} className="card-moj p-5">
                <dt className="font-serif text-lg text-moj-purple-deep mb-2">{f.q}</dt>
                <dd className="text-sm text-moj-charcoal/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* TRUST & TRANSPARENCY */}
      <section className="py-16 gradient-purple text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Cross size={36} className="text-moj-gold mx-auto mb-3"/>
          <h2 className="font-serif text-3xl text-white mb-3">Giving with Confidence</h2>
          <p className="text-white/90 mb-4">
            Ministry of Jesus Ireland is registered with the Charities Regulator in Ireland.
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-moj-gold/30 px-5 py-2 rounded-full text-moj-gold font-bold">
            Registered Charity Number: <span className="text-white">20207036</span>
          </div>
          <p className="text-white/80 mt-6 max-w-2xl mx-auto text-sm">
            Donations are used to support the charitable purposes, ministry activities, church administration, outreach work and community service
            of Ministry of Jesus Ireland. Proper records are maintained for donations and church finances in line with charity governance and reporting responsibilities.
          </p>
          <p className="text-xs text-white/60 mt-4">
            Charitable tax exemption / CHY details will be displayed once approved by Revenue.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/contact" className="btn-gold">Contact Us <ArrowRight size={16}/></Link>
            <Link to="/about" className="btn-outline !text-white !border-moj-gold hover:!bg-moj-gold hover:!text-moj-purple-deep">About the Church</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
